import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { Outlet, useLocation } from 'react-router';
import { useAnalytics } from '@/analytics/useAnalytics';
import { ConsentBanner } from '@/components/ConsentBanner';
import { useThemeColorMeta } from '@/hooks/useThemeColorMeta';
import { Footer } from './Footer';
import { Header } from './Header';

/**
 * The application shell: skip link, header, routed page, footer.
 *
 * Also restores scroll position to the top on navigation. A single-page app does not
 * do this by default, which leaves users landing halfway down a new page.
 */
export function Layout() {
  const { pathname } = useLocation();

  useThemeColorMeta();
  useAnalytics();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      <Link
        href="#main"
        sx={{
          position: 'absolute',
          left: 8,
          top: -64,
          zIndex: (t) => t.zIndex.appBar + 1,
          px: 2,
          py: 1,
          borderRadius: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          textDecoration: 'none',
          '&:focus': { top: 8 },
        }}
      >
        Skip to content
      </Link>

      <Header />

      <Box component="main" id="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>

      <Footer />

      {/*
        Last in the DOM because it is last on the screen. It is fixed to the bottom of
        the viewport, so a keyboard user reaches it after the page rather than before it,
        which is the order they see.
      */}
      <ConsentBanner />
    </Box>
  );
}
